import {
  Badge,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import type { PayrollListItem } from "@/modules/hr/payroll/domain/types";
import { getInitials } from "@/shared/lib/utils";
import { RowActions } from "@/shared/ui/RowActions";

const MONTH_NAMES = [
  "",
  "ມ.ກ.",
  "ກ.ພ.",
  "ມ.ນ.",
  "ມ.ສ.",
  "ພ.ສ.",
  "ມ.ຖ.",
  "ກ.ກ.",
  "ສ.ຫ.",
  "ກ.ຍ.",
  "ຕ.ລ.",
  "ພ.ຈ.",
  "ທ.ວ.",
];

function periodLabel(year: number, month: number): string {
  const m = MONTH_NAMES[month];
  return m ? `${m} ${year}` : `${year}/${month}`;
}

function formatPaidAt(paidAt: string | null): string {
  if (!paidAt) return "—";
  try {
    const d = new Date(paidAt);
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return paidAt;
  }
}

const statusLabel: Record<PayrollListItem["status"], string> = {
  draft: "ຮ່າງ",
  confirmed: "ຢືນຢັນ",
  paid: "ຊຳລະແລ້ວ",
};

type PayrollHistoryTableProps = {
  data: PayrollListItem[];
  isLoading?: boolean;
  isError?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange?: (id: string, desc: boolean) => void;
  onSlip: (row: PayrollListItem) => void;
  onDetails: (row: PayrollListItem) => void;
};

export function PayrollHistoryTable({
  data,
  isLoading,
  isError,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onSlip,
  onDetails,
}: PayrollHistoryTableProps) {
  const columns: TanstackReactTable.ColumnDef<PayrollListItem>[] = [
    createSortableColumn<PayrollListItem>("periodYear", "ເດືອນທີ່ຈ່າຍ", {
      size: 120,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {periodLabel(row.original.periodYear, row.original.periodMonth)}
        </span>
      ),
    }),
    createSortableColumn<PayrollListItem>("employeeName", "ພະນັກງານ", {
      size: 200,
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <span
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-muted font-medium text-muted-foreground text-xs"
            aria-hidden
          >
            {getInitials(row.original.employeeName ?? "")}
          </span>
          <div>
            <div className="font-medium">{row.original.employeeName}</div>
            <div className="text-muted-foreground text-xs">
              {row.original.employeeId.slice(0, 8)}
            </div>
          </div>
        </div>
      ),
    }),
    createSortableColumn<PayrollListItem>("departmentName", "ພະແນກ", {
      size: 140,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">{row.original.departmentName ?? "—"}</span>
      ),
    }),
    createSortableColumn<PayrollListItem>("positionTitle", "ຕໍາແໜ່ງ", {
      size: 140,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">{row.original.positionTitle ?? "—"}</span>
      ),
    }),
    createSortableColumn<PayrollListItem>("netSalary", "ຍອດສຸດທິ", {
      size: 120,
      cell: ({ row }) => (
        <span className="text-right font-medium tabular-nums">
          {row.original.netSalary.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    }),
    createSortableColumn<PayrollListItem>("status", "ສະຖານະ", {
      size: 100,
      cell: ({ row }) => (
        <Badge
          variant={
            row.original.status === "paid"
              ? "default"
              : row.original.status === "confirmed"
                ? "secondary"
                : "outline"
          }
        >
          {statusLabel[row.original.status]}
        </Badge>
      ),
    }),
    createSortableColumn<PayrollListItem>("paidAt", "ວັນທີຈ່າຍ", {
      size: 120,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">{formatPaidAt(row.original.paidAt)}</span>
      ),
    }),
    {
      id: "actions",
      header: "ການກະທຳ",
      cell: ({ row }) => (
        <RowActions
          maxInline={2}
          actions={[
            { label: "ໃບ slip", onClick: () => onSlip(row.original) },
            { label: "ລາຍລະອຽດ", onClick: () => onDetails(row.original), variant: "outline" },
          ]}
        />
      ),
      size: 160,
    },
  ];

  if (isError) {
    return (
      <div className="overflow-x-auto rounded-xl border bg-card p-8 text-center text-destructive">
        ເກີດຂໍ້ຜິດພາດໃນການໂຫຼດຂໍ້ມູນ.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border bg-card">
      <DataTable<PayrollListItem, unknown>
        noDataMessage="ບໍ່ພົບຂໍ້ມູນ."
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={
          onSortingChange
            ? (sorting) => {
                const s = sorting[0];
                if (s?.id) onSortingChange(s.id, !!s.desc);
              }
            : undefined
        }
      />
    </div>
  );
}
